import { Button, CheckIcon, Form, Heading, LinkButton, LoadableContentContainer, WexLogo } from "@wexinc/wex-ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { PasswordRequestInfo, useCopSecurityService } from "services/security";
import BlobSVG from "./BlobSVG";
import mainImage from "./MainImage.png";
import { useParams } from "react-router-dom";
import { useErrorBoundary } from "react-error-boundary";
import PasswordConfirmPassword, {
  getSchema as getPasswordConfirmPasswordSchema,
} from "../components/PasswordConfirmPassword";
import { CopRoutes } from "app/routes";
import { ApiError, FieldValidationsError, useAppStatus } from "@wexinc/wex-common-web";

const APIUserErrorMessage = (props: { message: string }) => {
  return <div className="text-sm text-utility-critical-60 max-w-[300px]">{props.message}</div>;
};

const PasswordReset = () => {
  const params = useParams() as { passwordResetId?: number };
  const [loading, setIsLoading] = useState(true);
  const [isReset, setIsReset] = useState(false);
  const [apiUserError, setApiUserError] = useState<string>();
  const copSecurityService = useCopSecurityService();
  const appStatus = useAppStatus();
  const securityToken = copSecurityService.getSecurityTokenFromUrl();
  const [passwordRequestInfo, setPasswordRequestInfo] = useState<PasswordRequestInfo>();
  const { showBoundary } = useErrorBoundary();

  if (!params.passwordResetId) {
    throw Error("Expected password reset id");
  }

  async function handleSubmit({ password }: { password: string }) {
    setIsLoading(true);
    try {
      await copSecurityService.resetPassword(params.passwordResetId!, password, securityToken);
      setIsReset(true);
    } catch (e) {
      if (e instanceof FieldValidationsError) {
        if ("password" in e.fieldValidationMessages) {
          setApiUserError(e.fieldValidationMessages["password"]);
          return;
        }
      }
      throw e;
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    // Annoying to have to do, but in the authorized area of the app, securityService takes care of this.
    appStatus.setAppStatusAction("reset");

    copSecurityService
      .getPasswordResetRequestInfo(params.passwordResetId!, securityToken)
      .then((info) => {
        setPasswordRequestInfo(info);
        setIsLoading(false);
      })
      .catch((error) => {
        if (error instanceof ApiError) {
          if (error.code === 404 || error.code === 403) {
            appStatus.setGlobalMessage(error.message);
          } else {
            showBoundary(Error("Unexpeced error."));
          }
        } else {
          showBoundary(Error("Unexpeced error."));
        }
      });
  }, []);

  return (
    <>
      <LoadableContentContainer
        showLoading={loading}
        message="Loading..."
      />
      <div className="flex h-full items-center mx-auto">
        <div className="flex items-center mx-auto">
          <section className="ml-[20px]">
            <WexLogo
              height="40px"
              width="124px"
            />
            <div className="pb-10 pt-2 text-neutral-dark-60 text-lg-plus font-semibold">Customer Operations Portal</div>

            {!isReset && passwordRequestInfo && (
              <>
                <Heading
                  size="medium"
                  className="mb-4 text-neutral-dark-60"
                >
                  New Password
                </Heading>
                <Form
                  className="relative"
                  onSubmit={handleSubmit}
                  options={{
                    resolver: yupResolver(getPasswordConfirmPasswordSchema(passwordRequestInfo.email)),
                  }}
                  onRealtimeChanges={() => {
                    if (apiUserError) {
                      setApiUserError(undefined);
                    }
                  }}
                >
                  <>
                    <PasswordConfirmPassword username={passwordRequestInfo.email} />
                    {apiUserError ? <APIUserErrorMessage message={apiUserError} /> : null}
                    <Button
                      className="w-full !h-14 mt-10"
                      testId="reset-password"
                      type="submit"
                    >
                      Reset password
                    </Button>
                  </>
                </Form>
              </>
            )}
            {isReset && (
              <>
                <div className="flex items-center">
                  <CheckIcon className="mr-2 text-utility-success-50 fill-current" />
                  <Heading size="medium">Password reset</Heading>
                </div>
                <LinkButton
                  testId="login-now"
                  size="small"
                  to={`${CopRoutes.copLogin}?email=${encodeURIComponent(passwordRequestInfo!.email)}`}
                  className="ml-4 mt-4"
                >
                  Login now
                </LinkButton>
              </>
            )}
          </section>
          <section className="relative ml-[110px]">
            <img
              src={mainImage}
              alt="Illustration"
              className="relative z-10"
            />
          </section>
        </div>
      </div>

      <BlobSVG
        width="596"
        height="297"
        viewBox="0 0 596 297"
        className="absolute bottom-0 right-0"
        opacity="0.5"
      />

      <BlobSVG
        width="565"
        height="285"
        viewBox="0 0 565 285"
        className="absolute bottom-0 right-0"
        opacity="1"
      />
    </>
  );
};

export default PasswordReset;
